import React, { useState } from 'react'

import type { ExploitConfig } from '../utils/exploitPayloads'
import { getSeverityColor } from '../utils/exploitPayloads'

import ExploitButton from './ExploitButton'
import styles from './ExploitCard.module.css'

interface ExploitCardProps {
  config: ExploitConfig
  index: number
  onExecute: (input?: string) => void
  isRunning?: boolean
  disabled?: boolean
}

const ExploitCard: React.FC<ExploitCardProps> = ({
  config,
  index,
  onExecute,
  isRunning = false,
  disabled = false,
}) => {
  const [inputValue, setInputValue] = useState(config.inputDefault || '')

  const handleExecute = () => {
    if (config.requiresInput) {
      onExecute(inputValue)
    } else {
      onExecute()
    }
  }

  const severityColor = getSeverityColor(config.severity)
  const buttonVariant = config.severity === 'critical' || config.severity === 'high' ? 'danger' : config.severity === 'medium' ? 'warning' : 'info'

  return (
    <div
      className={`${styles.card} ${styles[config.severity]}`}
      style={{ '--severity-color': severityColor } as React.CSSProperties}
    >
      <div className={styles.warningStripes} />

      <div className={styles.header}>
        <div className={styles.badge}>
          <span className={styles.badgeNumber}>{String(index + 1).padStart(2, '0')}</span>
        </div>
        <div className={styles.titleSection}>
          <h3 className={styles.title}>{config.name}</h3>
          <span className={`${styles.severityBadge} ${styles[config.severity]}`}>
            {config.severity.toUpperCase()}
          </span>
        </div>
      </div>

      <div className={styles.content}>
        <p className={styles.description}>{config.description}</p>

        {config.requiresInput && (
          <div className={styles.inputGroup}>
            <label className={styles.inputLabel}>{config.inputLabel}</label>
            <input
              type="text"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              className={styles.input}
              placeholder={config.inputDefault}
            />
          </div>
        )}

        <div className={styles.actions}>
          <ExploitButton
            variant={buttonVariant}
            onClick={handleExecute}
            disabled={disabled || isRunning}
          >
            {isRunning ? 'EXECUTING...' : disabled ? 'EXTENSION NOT FOUND' : 'EXECUTE'}
          </ExploitButton>
        </div>
      </div>
    </div>
  )
}

export default ExploitCard
